import * as React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { TextLayer } from 'pdfjs-dist';
import { DocumentMimeKind } from '../types';
import { applyHitHighlights, FindHit } from '../locate/find-engine';

export function usePdfPage(args: {
    mimeKind: DocumentMimeKind;
    loading: boolean;
    pageIndex: number;
    scale: number;
    pdfDocRef: React.MutableRefObject<any>;
    containerRef: React.RefObject<HTMLDivElement>;
    lastFitRef: React.MutableRefObject<number>;
    lastBoxRef: React.MutableRefObject<{ w: number; h: number }>;
    setFitScale: (n: number) => void;
    hitsRef: React.MutableRefObject<FindHit[]>;
    activeIndexRef: React.MutableRefObject<number>;
    pendingFocusRef: React.MutableRefObject<boolean>;
    panHitIntoView: (els: ArrayLike<Element>) => boolean;
}) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const textLayerRef = useRef<HTMLDivElement>(null);
    const textLayerInstRef = useRef<{ cancel: () => void } | null>(null);
    const textDivsRef = useRef<HTMLElement[]>([]);
    const renderTaskRef = useRef<any>(null);
    const renderSeqRef = useRef(0);
    const focusRafRef = useRef(0);

    const cancel = useCallback(() => {
        if (renderTaskRef.current) {
            try { renderTaskRef.current.cancel(); } catch { }
        }
    }, []);

    const paintHighlights = useCallback(() => {
        const textLayer = textLayerRef.current;
        const divs = textDivsRef.current;

        if (!textLayer || !divs.length) {
            return;
        }
        const allHits = args.hitsRef.current;
        const active = allHits[args.activeIndexRef.current] || null;
        const pageHits = allHits.filter(h => h.pageIndex === args.pageIndex);
        applyHitHighlights(textLayer, divs, pageHits, active && active.pageIndex === args.pageIndex ? active : null);

        if (!args.pendingFocusRef.current) {
            return;
        }

        if (focusRafRef.current) {
            cancelAnimationFrame(focusRafRef.current);
        }

        focusRafRef.current = requestAnimationFrame(() => {
            focusRafRef.current = 0;

            if (!args.pendingFocusRef.current) {
                return;
            }

            const activeNodes = textLayer.querySelectorAll('.findHitActive');
            const target = activeNodes.length ? activeNodes : textLayer.querySelectorAll('.findHit');

            if (!target.length) {
                return;
            }

            if (args.panHitIntoView(target)) {
                args.pendingFocusRef.current = false;
            }
        });
    }, [args]);

    const renderPdfPage = useCallback(async () => {
        const pdf = args.pdfDocRef.current;
        const canvas = canvasRef.current;
        const container = args.containerRef.current;
        if (!pdf || !canvas || !container || args.mimeKind !== 'pdf') {
            return;
        }

        const boxW = container.clientWidth;
        const boxH = container.clientHeight;
        if (boxW < 40 || boxH < 40) {
            return;
        }

        const page = await pdf.getPage(args.pageIndex + 1);
        const unscaled = page.getViewport({ scale: 1 });
        const availableW = Math.max(boxW - 24, 100);
        const availableH = Math.max(boxH - 24, 100);
        const fitted = Math.min(availableW / unscaled.width, availableH / unscaled.height);
        const nextFit = fitted > 0 ? fitted : 1;
        if (Math.abs(nextFit - args.lastFitRef.current) > 0.002) {
            args.lastFitRef.current = nextFit;
            args.setFitScale(nextFit);
        }
        args.lastBoxRef.current = { w: boxW, h: boxH };
        const renderSeq = ++renderSeqRef.current;

        const effectiveScale = nextFit * args.scale;
        const viewport = page.getViewport({ scale: effectiveScale * (window.devicePixelRatio || 1) });
        const cssViewport = page.getViewport({ scale: effectiveScale });

        const context = canvas.getContext('2d');
        if (!context) {
            return;
        }

        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${cssViewport.width}px`;
        canvas.style.height = `${cssViewport.height}px`;

        cancel();

        const task = page.render({ canvas, canvasContext: context, viewport });
        renderTaskRef.current = task;
        try {
            await task.promise;
        } catch {
            return;
        }
        if (renderSeq !== renderSeqRef.current) {
            return;
        }

        const textLayer = textLayerRef.current;

        if (textLayer) {
            if (textLayerInstRef.current) {
                try { textLayerInstRef.current.cancel(); } catch { }
                textLayerInstRef.current = null;
            }
            textLayer.innerHTML = '';
            const scaleFactor = String(cssViewport.scale);
            textLayer.style.setProperty('--scale-factor', scaleFactor);
            textLayer.style.setProperty('--user-unit', '1');
            textLayer.style.setProperty('--total-scale-factor', scaleFactor);
            textLayer.style.setProperty('--scale-round-x', '1px');
            textLayer.style.setProperty('--scale-round-y', '1px');
            const pageEl = canvas.parentElement;

            if (pageEl) {
                pageEl.style.setProperty('--scale-factor', scaleFactor);
                pageEl.style.width = `${cssViewport.width}px`;
                pageEl.style.height = `${cssViewport.height}px`;
            }
            try {
                const textContent = await page.getTextContent();

                if (renderSeq !== renderSeqRef.current) {
                    return;
                }
                const layer = new TextLayer({
                    textContentSource: textContent,
                    container: textLayer,
                    viewport: cssViewport
                });
                textLayerInstRef.current = layer;
                await layer.render();

                if (renderSeq !== renderSeqRef.current) {
                    return;
                }
                textLayer.style.width = `${cssViewport.width}px`;
                textLayer.style.height = `${cssViewport.height}px`;
                textLayer.style.left = '0px';
                textLayer.style.top = '0px';
                textDivsRef.current = layer.textDivs || [];
                paintHighlights();
            } catch {
                textDivsRef.current = [];
            }
        }
    }, [args, cancel, paintHighlights]);

    useEffect(() => {
        if (!args.loading && args.mimeKind === 'pdf' && args.pdfDocRef.current) {
            renderPdfPage();
        }
    }, [args.loading, args.mimeKind, args.pageIndex, args.scale, renderPdfPage, args.pdfDocRef]);

    useEffect(() => {
        if (!args.containerRef.current) {
            return;
        }
        let raf = 0;
        const observer = new ResizeObserver(() => {
            if (args.mimeKind !== 'pdf' || !args.pdfDocRef.current || !args.containerRef.current) {
                return;
            }
            const w = args.containerRef.current.clientWidth;
            const h = args.containerRef.current.clientHeight;
            if (Math.abs(w - args.lastBoxRef.current.w) < 2 && Math.abs(h - args.lastBoxRef.current.h) < 2) {
                return;
            }
            if (raf) {
                cancelAnimationFrame(raf);
            }
            raf = requestAnimationFrame(() => {
                raf = 0;
                renderPdfPage();
            });
        });
        observer.observe(args.containerRef.current);
        return () => {
            if (raf) {
                cancelAnimationFrame(raf);
            }
            observer.disconnect();
        };
    }, [args.mimeKind, renderPdfPage, args.containerRef, args.pdfDocRef, args.lastBoxRef]);

    useEffect(() => {
        return () => {
            if (focusRafRef.current) {
                cancelAnimationFrame(focusRafRef.current);
            }
        };
    }, []);

    const clearTextDivs = useCallback(() => {
        textDivsRef.current = [];
    }, []);

    return {
        canvasRef,
        textLayerRef,
        paintHighlights,
        cancel,
        clearTextDivs
    };
}
